import type { RoutineExercise } from '@fitness/api-client';
import { useQueries } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Eye, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { ExerciseThumb } from '@/components/exercise/ExerciseThumb';
import { prescriptionLine } from '@/components/routine/templateFormat';
import { PreviousBest } from '@/components/workout/ExerciseBlock';
import { ExerciseTechniqueSheet } from '@/components/workout/ExerciseTechniqueSheet';
import { setTargetLabel } from '@/components/workout/format';
import { describeError } from '@/lib/api';
import { DAY_PREVIEW } from '@/lib/i18n/training';
import { exerciseBestOptions } from '@/lib/queries/progress';
import { useActiveSession, useRoutine } from '@/lib/queries/workouts';

/**
 * Vista previa de un día de rutina, de sólo lectura.
 *
 * Mirar qué toca hoy o repasar la técnica no debe crear una sesión: aquí no se
 * escribe nada, ni en el dispositivo ni en el servidor. Lo único que empieza el
 * entreno es «Empezar entreno», que lleva a `/train/session/new` con los mismos
 * parámetros que el ▶ de la portada.
 */
export const Route = createFileRoute('/_app/train/day/$routineId/$dayId')({
  component: DayPreviewPage,
});

/**
 * Última marca de cada ejercicio. Es la misma consulta que usa la sesión
 * (`limit=1`), pero con su propio ámbito y sin congelarla: la sesión la fija
 * para no compararse consigo misma; aquí interesa la más reciente.
 */
function previewBestOptions(exerciseId: string) {
  return {
    ...exerciseBestOptions('preview', exerciseId),
    staleTime: 60_000,
    refetchOnMount: true,
  } as const;
}

function DayPreviewPage() {
  const { routineId, dayId } = Route.useParams();
  const routine = useRoutine(routineId);
  const active = useActiveSession();

  const day = routine.data?.days.find((candidate) => candidate.id === dayId) ?? null;
  const exercises = useMemo(
    () => [...(day?.exercises ?? [])].sort((a, b) => a.position - b.position),
    [day],
  );

  const bestQueries = useQueries({
    queries: exercises.map((item) => previewBestOptions(item.exercise.id)),
  });

  const [technique, setTechnique] = useState<RoutineExercise | null>(null);

  const resuming = active != null && active.finishedAt === null && active.routineDayId === dayId;

  const back = (
    <Link
      to="/train"
      aria-label={DAY_PREVIEW.back}
      className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
    >
      <ArrowLeft className="size-5" aria-hidden="true" />
    </Link>
  );

  if (routine.isPending) {
    return (
      <>
        <PageHeader title="Día de entreno" action={back} />
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  if (routine.isError || day === null) {
    return (
      <>
        <PageHeader title="Día de entreno" action={back} />
        <ErrorBanner
          title={DAY_PREVIEW.loadError}
          message={routine.isError ? describeError(routine.error) : DAY_PREVIEW.notFound}
          onRetry={routine.isError ? () => void routine.refetch() : undefined}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={day.name} subtitle={routine.data.name} action={back} />

      {/* En móvil, hueco para la barra fija de «Empezar entreno». */}
      <div className="flex flex-col gap-3 pb-24 md:pb-0">
        {day.notes !== null && day.notes !== '' ? (
          <p className="rounded-xl bg-surface-raised px-4 py-3 text-sm whitespace-pre-line text-ink-muted">
            {day.notes}
          </p>
        ) : null}

        {exercises.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-4 py-8 text-center text-sm text-ink-muted">
            {DAY_PREVIEW.empty}
          </p>
        ) : (
          <>
            <p className="px-1 text-xs text-ink-faint">
              {DAY_PREVIEW.exercises(exercises.length)} · {DAY_PREVIEW.techniqueHint}
            </p>
            <ol className="flex flex-col gap-2" data-tour="day-exercises">
              {exercises.map((item, index) => (
                <li key={item.id}>
                  <PreviewExercise
                    item={item}
                    index={index}
                    best={bestQueries[index]?.data}
                    onOpen={() => setTechnique(item)}
                  />
                </li>
              ))}
            </ol>
          </>
        )}

        {/* Móvil: fija encima de la barra de navegación. Desde `md`, pegada al
            borde de abajo mientras se baja por la lista. */}
        <div
          data-tour-avoid
          className="fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px))] z-30 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur xs:px-6 md:sticky md:inset-x-auto md:bottom-6 md:mt-3 md:rounded-2xl md:border md:bg-surface-raised/95 md:p-3"
        >
          <Link
            to="/train/session/new"
            search={{ routineId, routineDayId: dayId }}
            data-tour="day-start"
            className="mx-auto flex min-h-13 w-full max-w-tablet items-center justify-center gap-2 rounded-xl bg-brand px-5 text-base font-bold text-brand-ink transition-colors hover:bg-brand-strong active:bg-brand-strong"
          >
            <Play className="size-5" aria-hidden="true" />
            {resuming ? DAY_PREVIEW.resume : DAY_PREVIEW.start}
          </Link>
        </div>
      </div>

      <ExerciseTechniqueSheet
        open={technique !== null}
        onClose={() => setTechnique(null)}
        exerciseId={technique?.exercise.id ?? ''}
        exerciseName={technique?.exercise.nameEs ?? ''}
      />
    </>
  );
}

/** Un ejercicio del día: toda la tarjeta abre la técnica. */
function PreviewExercise({
  item,
  index,
  best,
  onOpen,
}: {
  item: RoutineExercise;
  index: number;
  best: Parameters<typeof PreviousBest>[0]['best'] | undefined;
  onOpen: () => void;
}) {
  const detail = item.targetSetsDetail ?? [];
  // La línea de prescripción ya trae las repeticiones (y las cargas) serie a
  // serie; el desglose sólo añade algo cuando alguna serie fija su RPE.
  const perSet = detail.some((set) => set.rpe != null) ? detail : [];
  const cardio =
    item.exercise.category === 'cardio' ||
    item.targetDurationSeconds != null ||
    item.targetDistanceM != null;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-tour="day-exercise"
      aria-label={DAY_PREVIEW.technique(item.exercise.nameEs)}
      className="flex w-full items-start gap-3 rounded-2xl border border-line bg-surface-raised p-3 text-left transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
    >
      <span className="relative shrink-0">
        <ExerciseThumb media={item.exercise.media} name={item.exercise.nameEs} size="sm" />
        <span
          aria-hidden="true"
          className="absolute right-0 bottom-0 flex size-5 items-center justify-center rounded-tl-lg rounded-br-xl bg-black/60 text-white"
        >
          <Eye className="size-3" />
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="shrink-0 text-xs font-semibold text-ink-faint tabular-nums">
            {index + 1}.
          </span>
          <span className="line-clamp-2 text-sm font-semibold text-ink">
            {item.exercise.nameEs}
          </span>
        </span>
        <span className="mt-0.5 block text-xs text-ink-muted">{prescriptionLine(item)}</span>
        {perSet.length > 0 ? (
          <span className="mt-1 flex flex-wrap gap-1">
            {perSet.map((set, position) => (
              <span
                key={position}
                className="rounded-md bg-surface px-1.5 py-0.5 text-[11px] text-ink-muted tabular-nums"
              >
                {DAY_PREVIEW.setN(position + 1)}: {setTargetLabel(set, item.exercise.unilateral)}
              </span>
            ))}
          </span>
        ) : null}
        {item.supersetGroup !== null ? (
          <span className="mt-0.5 block text-xs text-brand">
            {DAY_PREVIEW.superset(item.supersetGroup + 1)}
          </span>
        ) : null}
        {item.notes !== null && item.notes !== '' ? (
          <span className="mt-0.5 block text-xs text-ink-faint">{item.notes}</span>
        ) : null}
        {/* El 1RM no dice nada de una serie de cardio. */}
        {best === undefined || cardio ? null : (
          <span className="mt-1 block">
            <PreviousBest best={best} label={DAY_PREVIEW.lastMark} emptyText={null} />
          </span>
        )}
      </span>
    </button>
  );
}
